'use client';
import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { FlagImg } from '@/components/site/flag-img';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger } from '@/components/ui/select';
import { COUNTRY_COOKIE, isCountryCode } from '@/lib/geo';
import { DEFAULT_PHONE_COUNTRY, formatNational, phoneCountryOptions, splitPhone, toE164, type CountryCode } from '@/lib/phone-countries';
import { cn } from '@/lib/utils';

/**
 * The one phone field: an explicit country next to the national number, storing
 * canonical E.164.
 *
 * WHY THE COUNTRY IS A CONTROL AND NOT A GUESS. This used to be a bare input with a
 * Brazilian mask, and the API's `normalizePhone` read anything without a leading `+`
 * as Brazilian. So a Canadian typing 4389280106 was stored, formatted and finally
 * SENT as +554389280106 — a real, unrelated line in Brazil. That is an LGPD incident
 * (a patient's appointment details delivered to a stranger) and a spam report against
 * a new WhatsApp number, and it was reachable from the public signup form, the guest
 * booking funnel and both lead forms, all of which feed a WhatsApp send. The country
 * is now always stated by the user, never inferred from the shape of the digits.
 *
 * Value contract: `value`/`defaultValue`/`onChange` all speak canonical E.164
 * (`+14389280106`), which is what the column stores and what `normalizePhone` passes
 * through verbatim. `onChange` hands over the string, NOT a ChangeEvent — a composite
 * of two controls has no single native event to forward.
 *
 * Validation is `isPossible` (right length for the country), not `isValid` (a known
 * assigned range): libphonenumber's range data lags carrier allocations and refusing
 * a real new number at signup is worse than accepting an unassigned one. It surfaces
 * through the visible input's own `setCustomValidity`, so a form submit reports it
 * the way every other required field does.
 */
export function PhoneInput({
  name,
  id,
  value,
  defaultValue,
  onChange,
  onRawChange,
  placeholder,
  required = false,
  disabled = false,
  autoFocus = false,
  className,
  containerClassName,
  'aria-label': ariaLabel,
}: {
  /** Hidden input name, so the E.164 value lands in FormData like any other field. */
  name?: string;
  id?: string;
  /** Controlled: canonical E.164. */
  value?: string;
  /** Uncontrolled: E.164, or a legacy value which is parsed with the default country. */
  defaultValue?: string;
  /** Receives canonical E.164, or '' while the number is empty or not yet possible. */
  onChange?: (e164: string) => void;
  /** Receives the formatted national value, including while it is incomplete. */
  onRawChange?: (national: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  containerClassName?: string;
  'aria-label'?: string;
}) {
  const locale = useLocale();
  const t = useTranslations('common');
  const options = React.useMemo(() => phoneCountryOptions(locale), [locale]);
  const numberRef = React.useRef<HTMLInputElement>(null);

  const initial = React.useMemo(
    () => splitPhone(value ?? defaultValue ?? ''),
    // Only the first render seeds the split; later edits flow through local state so
    // the caret is not yanked around by a reformat on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [country, setCountry] = React.useState<CountryCode>(initial.country);
  const [national, setNational] = React.useState(() =>
    formatNational(initial.national, initial.country),
  );

  // IP-detected country as the STARTING default, and only for a field that opened
  // empty — never a correction of a number the user already has stored. Same signal
  // and same 'BR' fallback as geo.ts's marketForCountry.
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (seededRef.current || initial.national || (value ?? defaultValue)) return;
    seededRef.current = true;
    const raw = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${COUNTRY_COOKIE}=`))
      ?.split('=')[1];
    if (isCountryCode(raw) && raw !== DEFAULT_PHONE_COUNTRY) setCountry(raw as CountryCode);
  }, [initial.national, value, defaultValue]);

  const e164 = toE164(national, country);
  const incomplete = national.replace(/\D/g, '').length > 0 && !e164;

  // Report the "possible" check through native form validation, so the browser blocks
  // submit and focuses the field exactly as it does for a missing required input.
  React.useEffect(() => {
    numberRef.current?.setCustomValidity(incomplete ? t('phone.incomplete') : '');
  }, [incomplete, t]);

  const emit = (nextNational: string, nextCountry: CountryCode) => {
    const formatted = formatNational(nextNational, nextCountry);
    setNational(formatted);
    setCountry(nextCountry);
    onChange?.(toE164(nextNational, nextCountry) ?? '');
    onRawChange?.(formatted);
  };

  const selected = options.find((o) => o.code === country);

  return (
    <div
      className={cn(
        // The pill IS this wrapper: both children are borderless so there is one
        // focus glow, per the form-control contract.
        'flex h-11 items-center rounded-lg border border-input bg-white transition-[border-color,box-shadow]',
        'focus-within:border-medlux focus-within:shadow-field-focus',
        disabled && 'cursor-not-allowed bg-muted',
        containerClassName,
      )}
    >
      <Select
        value={country}
        // Inside a <form>, Radix mirrors the value into a hidden native <select> whose
        // <option>s only exist once the dropdown has opened. A controlled change before
        // that (the geo cookie seeding CA over the initial BR) finds no matching option,
        // the native select falls back to '' and Radix reports it as onValueChange('').
        // Every real pick is a country code, so an empty value is never the user.
        onValueChange={(c) => {
          if (c) emit(national, c as CountryCode);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={t('phone.countryLabel')}
          className="h-full w-auto shrink-0 gap-1.5 border-0 bg-transparent pl-3 pr-2 shadow-none focus-visible:border-0 focus-visible:shadow-none"
        >
          <span className="flex items-center gap-1.5">
            <FlagImg code={country.toLowerCase()} width={20} height={15} />
            <span className="tabular-nums">+{selected?.dial ?? ''}</span>
          </span>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((o, i) => (
            <React.Fragment key={o.code}>
              {/* The pinned markets sit above the line; everything else follows
                  alphabetically in the reader's own language. */}
              {i > 0 && options[i - 1]?.pinned && !o.pinned && <SelectSeparator />}
              <SelectItem value={o.code}>
                <span className="flex items-center gap-2">
                  <FlagImg code={o.code.toLowerCase()} width={18} height={13} />
                  <span>{o.name}</span>
                  <span className="text-muted-foreground tabular-nums">+{o.dial}</span>
                </span>
              </SelectItem>
            </React.Fragment>
          ))}
        </SelectContent>
      </Select>

      <span aria-hidden className="h-5 w-px shrink-0 bg-border" />

      <input
        ref={numberRef}
        id={id}
        type="tel"
        inputMode="tel"
        maxLength={24}
        autoComplete="tel-national"
        value={national}
        onChange={(e) => emit(e.target.value, country)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        data-1p-ignore
        data-lpignore
        className={cn(
          'h-full min-w-0 flex-1 rounded-r-lg bg-transparent px-3 text-sm text-foreground outline-none',
          'placeholder:text-neutral-400 disabled:cursor-not-allowed disabled:text-muted-foreground',
          className,
        )}
      />
      {name && <input type="hidden" name={name} value={e164 ?? ''} />}
    </div>
  );
}
